import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const priorMonth = () => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().slice(0, 7);
};
const lastMonths = (n) => {
  const out = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < n; i++) {
    out.push(d.toISOString().slice(0, 7));
    d.setMonth(d.getMonth() - 1);
  }
  return out;
};
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fmtDt = (iso) => new Date(iso).toLocaleString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });

export default function VendorReports({ active }) {
  const [rows, setRows] = useState(null);
  const [month, setMonth] = useState(priorMonth());
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [fee, setFee] = useState([]);
  const [feeForm, setFeeForm] = useState({});

  const fetchFee = useCallback(() => {
    axios.get(`${API}/fee-check?months=6`).then((r) => {
      setFee(r.data.months.slice().reverse());
      const next = {};
      r.data.months.forEach((m) => {
        next[m.month] = { paid: m.alteva_reported_paid ?? "", fee: m.alteva_reported_fee ?? "" };
      });
      setFeeForm(next);
    }).catch(() => {});
  }, []);

  const setFeeVal = (m, k, v) => setFeeForm((p) => ({ ...p, [m]: { ...p[m], [k]: v } }));

  const saveFee = async (m) => {
    const f = feeForm[m] || {};
    try {
      await axios.put(`${API}/fee-check/${m}`, {
        alteva_reported_paid: f.paid === "" || f.paid == null ? null : Number(f.paid),
        alteva_reported_fee: f.fee === "" || f.fee == null ? null : Number(f.fee),
      });
      toast.success(`Fee check saved for ${m}`);
      fetchFee();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  const fetchRows = useCallback(() => {
    axios.get(`${API}/vendor-reports`).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { if (active === "vrep") { fetchRows(); fetchFee(); } }, [active, fetchRows, fetchFee]);

  const upload = async () => {
    if (!file || !month) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("month", month);
      fd.append("note", note);
      fd.append("file", file);
      await axios.post(`${API}/vendor-reports`, fd);
      toast.success(`${file.name} filed for ${month} — it exports with the binder`);
      setNote(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    }
    setBusy(false);
  };

  const download = async (r) => {
    try {
      const res = await axios.get(`${API}/vendor-reports/${r.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = r.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const remove = async (r) => {
    try {
      await axios.delete(`${API}/vendor-reports/${r.id}`);
      toast.success(`${r.filename} removed`);
      fetchRows();
    } catch {
      toast.error("Delete failed");
    }
  };

  const covered = new Set((rows || []).map((r) => r.month));

  return (
    <section className={`chapter utility${active === "vrep" ? " active" : ""}`} id="vrep">
      <div className="eyebrow">Working record · executes ECH-SOP-13.3 — Alteva's monthly report is the evidence behind the C-11 fee reconciliation</div>
      <h1>Alteva Reports</h1>
      <p className="lede">One slot per month for Alteva RCM's report (remittance detail, fee statement, dashboard export). Upload it, reconcile the fee against it — 25% on Assist-as-a-Service RCM claims per the 4/20/2026 agreement — and it ships inside every binder export under <span className="q">vendor_reports/</span> — the reconciliation is only as good as the evidence on file.</p>

      <div className="vrep-coverage" data-testid="vrep-coverage">
        {lastMonths(6).map((m) => (
          <span key={m} className={`vr-pill ${covered.has(m) ? "cleared" : "open"}`} data-testid={`vrep-month-${m}`}>{m} {covered.has(m) ? "✓" : "—"}</span>
        ))}
      </div>

      <div className="formcard" data-testid="vrep-form">
        <div className="formcard-title">File a monthly report</div>
        <div className="fld-grid three">
          <div className="fld"><label>Report month</label><input type="month" className="inp" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="vrep-month-input" /></div>
          <div className="fld"><label>File (PDF, CSV, XLSX — max 20 MB)</label><input ref={fileRef} type="file" className="inp" accept=".pdf,.csv,.xlsx,.xls,.txt" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="vrep-file-input" /></div>
          <div className="fld"><label>Note</label><input className="inp" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. remittance detail / fee statement" data-testid="vrep-note" /></div>
        </div>
        <button className="btn" onClick={upload} disabled={!file || !month || busy} data-testid="vrep-upload-btn">{busy ? "Uploading…" : "Upload report"}</button>
      </div>

      <h2>On file {rows ? `— ${rows.length} report${rows.length === 1 ? "" : "s"}` : ""}</h2>
      <table data-testid="vrep-table">
        <tbody>
          <tr><th>Month</th><th>File</th><th>Size</th><th>Note</th><th>Uploaded</th><th></th></tr>
          {rows === null ? (
            <tr><td colSpan={6}>Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={6}>Nothing on file yet — the reminders bar flags the prior month after the 5th.</td></tr>
          ) : (
            rows.map((r) => (
              <tr key={r.id}>
                <td className="q">{r.month}</td>
                <td>{r.filename}</td>
                <td>{fmtSize(r.size)}</td>
                <td>{r.note || "—"}</td>
                <td>{fmtDt(r.uploaded_at)}{r.recorded_by ? <div className="cap-sub">{r.recorded_by.name}</div> : null}</td>
                <td>
                  <button className="vr-link" onClick={() => download(r)} data-testid={`vrep-download-${r.id}`}>Download</button>{" "}
                  <button className="vr-link" onClick={() => remove(r)} data-testid={`vrep-delete-${r.id}`}>Delete</button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <h2>Fee variance check — 25% of paid claims (AaaS RCM rate)</h2>
      <p className="k-sub" style={{ maxWidth: 720 }}>Enter Alteva's reported paid-claims and fee figures from the monthly report above. The system computes the expected <b>25%</b> fee — the Assist-as-a-Service RCM rate per the M&amp;D/Alteva Service Agreement (4/20/2026) — using Alteva's reported paid when given, otherwise ECH's own award-paid data, and flags any gap beyond $1 / 0.5%. Claims billed at another contract rate (18% standard · 35% unlisted/advocacy · 50% legacy AR) should be reconciled at their applicable rate and any resulting variance explained in the note. Flags land on the reminders bar and in the daily nudge.</p>
      <table data-testid="fee-table">
        <tbody>
          <tr><th>Month</th><th>ECH paid (system)</th><th>Alteva reported paid</th><th>Expected fee (25%)</th><th>Alteva reported fee</th><th>Variance</th><th>Status</th><th></th></tr>
          {fee.map((f) => (
            <tr key={f.month}>
              <td className="q">{f.month}</td>
              <td>${Number(f.system_paid).toLocaleString()}{f.paid_gap != null && Math.abs(f.paid_gap) > 0.005 ? <div className="cap-sub">gap vs reported: ${f.paid_gap.toLocaleString()}</div> : null}</td>
              <td><input className="inp fee-inp" type="number" placeholder="—" value={feeForm[f.month]?.paid ?? ""} onChange={(e) => setFeeVal(f.month, "paid", e.target.value)} data-testid={`fee-paid-${f.month}`} /></td>
              <td>${Number(f.expected_fee).toLocaleString()}</td>
              <td><input className="inp fee-inp" type="number" placeholder="—" value={feeForm[f.month]?.fee ?? ""} onChange={(e) => setFeeVal(f.month, "fee", e.target.value)} data-testid={`fee-fee-${f.month}`} /></td>
              <td>{f.variance != null ? `$${f.variance.toLocaleString()}` : "—"}</td>
              <td><span className={`vr-pill ${f.status === "OK" ? "cleared" : f.status === "FLAG" ? "declined" : "open"}`} data-testid={`fee-status-${f.month}`}>{f.status}</span></td>
              <td><button className="vr-link" onClick={() => saveFee(f.month)} data-testid={`fee-save-${f.month}`}>Save</button></td>
            </tr>
          ))}
        </tbody>
      </table>

      <p style={{ color: "var(--mute)", fontSize: "13px" }}>Retention: 10 years with claim records. Missing prior-month report escalates on the reminders bar after the 5th and turns overdue after the 12th — the day-15 lender close depends on it.</p>
    </section>
  );
}
